import { CircularProgress, Grid, Typography, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { RouteConstants } from '../../../navigation/navigation-types'
import { EventDto } from '../../../services/api/sharedTypes/Event'
import ScreenLoadingPlaceholder from '../../../components/ScreenLoadingPlaceholder'
import { displayMessage } from '../../../services/redux/snackbar/snackbar-actions'
import { EventManager } from '../../../services/api/EventManager'
import { useAppDispatch } from '../../../hooks/reduxHooks'
import EventBasicInformation from '../../../components/event/EventBasicInformation'
import CarrierTable from '../../../components/event/tables/CarrierTable'
import BrigadierTable from '../../../components/event/tables/BrigadierTable'
import ProductTable from '../../../components/event/tables/ProductTable'
import { ProductManager } from '../../../services/api/ProductManager'
import {
    ContractorBriefDto,
    ContractorDto,
    ContractorOfEventDto,
    ContractorType,
} from '../../../services/api/sharedTypes/Contractor'
import { ContractorManager } from '../../../services/api/ContractorManager'
import { setSaveState } from '../../../services/redux/savestate/savestate-actions'
import { InvoiceDto, InvoicePaymentType } from '../../../services/api/sharedTypes/Invoice'
import { InvoiceManager } from '../../../services/api/InvoiceManager'
import EventStateHistory from '../../../components/event/EventStateHistory'
import EventInvoiceInfo from '../../../components/event/invoice/EventInvoiceInfo'
import EventSurveys from '../../../components/event//EventSurveys'
import CustomContainer from '../../../components/common/CustomContainer'
import { EmailManager } from '../../../services/api/EmailManager'
import { EmailTemplateDto } from '../../../services/api/sharedTypes/Email'
import {
    calculateTimeDifferenceInTimeSpan,
    convertTimeToLocaleTime,
    getTimeSpanFromFormattedTimeSpan,
} from '../../../utils/global-utils'
import { getUsedProducts, ProductQuantities } from '../../../utils/product-utils'
import { IdNameDto } from '../../../services/api/sharedTypes/Common'
import BottomBar from '../../../components/common/BottomBar'
import CustomTextField from '../../../components/common/CustomTextField'

interface EventDetailProps {
    /**
     * propId is id of event it is used when displaying event detail as dialog and we don't have id in url
     */
    propId?: string
    /**
     * closeDialog is function that closes dialog when event detail is displayed as dialog
     */
    closeDialog?: () => void
}

const EventDetail: React.FC<EventDetailProps> = ({ propId, closeDialog }: EventDetailProps) => {
    const { id } = useParams()
    const navigate = useNavigate()
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const finalId = propId ?? id // if propId is not null (eventDetail is in dialog) use it otherwise use id from url

    const [loadingData, setLoadingData] = useState<boolean>(true)
    const [loadingInvoiceData, setLoadingInvoiceData] = useState<boolean>(true)
    const [loadingTableData, setLoadingTableData] = useState<boolean>(false)
    const [loadingLookupData, setLoadingLookupData] = useState<boolean>(false)

    const [isProductChanged, setIsProductChanged] = useState<boolean>(false)
    const [isContractorChanged, setIsContractorChanged] = useState<boolean>(false)

    const [event, setEvent] = useState<EventDto>()
    const [oldEvent, setOldEvent] = useState<EventDto>()

    const [invoice, setInvoice] = useState<InvoiceDto>()

    const [productQuantities, setProductQuantities] = useState<ProductQuantities[]>([])
    const [areProductQuantitiesLoading, setAreProductQuantitiesLoading] = useState<boolean>(false)

    const [contractorList, setContractorList] = useState<ContractorBriefDto[]>()
    const [emailTemplatesList, setEmailTemplatesList] = useState<EmailTemplateDto[]>()

    const [lookupProducts, setLookupProducts] = useState<IdNameDto[]>()
    const [lookupCarriers, setLookupCarriers] = useState<IdNameDto[]>()
    const [lookupBrigadiers, setLookupBrigadiers] = useState<IdNameDto[]>()

    const canGoBack =
        event?.name === oldEvent?.name &&
            event?.notesForBrigadiers === oldEvent?.notesForBrigadiers &&
            event?.clientId === oldEvent?.clientId &&
            event?.contactPerson === oldEvent?.contactPerson &&
            event?.contractors.length === oldEvent?.contractors.length &&
            event?.products.length === oldEvent?.products.length &&
            event?.description === oldEvent?.description &&
            event?.orderState === oldEvent?.orderState &&
            !isProductChanged &&
            !isContractorChanged &&
            event?.paymentState === oldEvent?.paymentState
            ? true
            : false

    if (!finalId)
        return (
            <Typography variant='h4' color='error' textAlign='center' marginY={theme.spacing(3)}>
                Tato událost neexistuje
            </Typography>
        )

    useEffect(() => {
        dispatch(setSaveState(canGoBack))
    }, [canGoBack, dispatch])

    // gets total quantity of products and calculates used quantity of products. All quantities are set in productQuantities state in format {productId, productName, usedQuantity, totalQuantity}
    const getProductQuantities = async () => {
        if (!event) return
        setProductQuantities([])
        event.products.forEach(async (product) => {
            if (!product.productId || product.quantity <= 0) return
            const eventsOfProduct = await getEventsOfProduct(product.productId, event.startsAt, event.endsAt)
            if (!eventsOfProduct) return
            const usedQuantity =
                eventsOfProduct.events
                    .filter((localProduct) => localProduct.eventId !== event.id)
                    .filter((localProduct) => localProduct.isConfirmed)
                    .reduce((acc, localProduct) => acc + localProduct.quantity, 0) ?? 0
            const totalQuantity = eventsOfProduct.productTotalQuantity ?? 0
            setProductQuantities((prev) => [
                ...prev,
                { productId: product.productId, productName: product.productName, usedQuantity, totalQuantity },
            ])
        })
    }

    const recalculateOperatingTimeForEachProduct = () => {
        if (!event) return

        setEvent({
            ...event,
            products: event.products.map((product) => {
                const operatingTime = calculateTimeDifferenceInTimeSpan(event.startsAt, event.endsAt)
                return { ...product, operatingTime }
            }),
        })
    }

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true)
            try {
                const eventRes = await EventManager.getManager().getEventAsync(finalId)
                if (!eventRes) throw new Error('Událost nenalezena')
                if (!eventRes.contacts || eventRes.contacts.length === 0) {
                    setEvent({ ...eventRes, contacts: [{ name: '', phone: '', email: '' }] })
                    setOldEvent({ ...eventRes, contacts: [{ name: '', phone: '', email: '' }] })
                } else {
                    setEvent(eventRes)
                    setOldEvent(eventRes)
                }
            } catch (error) {
                setLoadingData(false)
                dispatch(displayMessage({ message: error, type: 'error' }))
                return navigate(RouteConstants.events)
            }
            setLoadingData(false)
        }

        const getContractors = async () => {
            setLoadingTableData(true)
            try {
                const contractorsRes = await ContractorManager.getManager().getContractorListAsync()
                if (!contractorsRes) throw new Error('Pracovníci nenalezeni')
                setContractorList(contractorsRes.contractors)
            } catch (error) {
                dispatch(displayMessage({ message: error, type: 'error' }))
                setLoadingTableData(false)
            }
            setLoadingTableData(false)
        }

        const getInvoice = async () => {
            setLoadingInvoiceData(true)
            try {
                const invoiceRes = await EventManager.getManager().getInvoiceAsync(finalId)
                if (invoiceRes) setInvoice(invoiceRes)
            } catch (error) {
                setInvoice(undefined)
                setLoadingInvoiceData(false)
            }
            setLoadingInvoiceData(false)
        }

        const getEmailTemplates = async () => {
            setLoadingInvoiceData(true)
            try {
                const emailTemplatesRes = await EmailManager.getManager().getEmailTemplatesListAsync()
                if (!emailTemplatesRes) throw new Error('Šablony emailů nenalezeny')
                setEmailTemplatesList(emailTemplatesRes.emailTemplates)
            } catch (error) {
                dispatch(displayMessage({ message: error, type: 'error' }))
                setLoadingInvoiceData(false)
            }
            setLoadingInvoiceData(false)
        }

        getContractors()
        getInvoice()
        getEmailTemplates()
        fetchData()
    }, [setLoadingData, setLoadingTableData])

    useEffect(() => {
        const getLookupData = async () => {
            setLoadingLookupData(true)
            try {
                const lookupProductsRes = await ProductManager.getManager().getProductsLookupAsync()
                if (!lookupProductsRes) throw new Error('Produkty nenalezeny')
                setLookupProducts(lookupProductsRes.items)
                const lookupCarriersRes = await ContractorManager.getManager().getCarriersLookupAsync()
                if (!lookupCarriersRes) throw new Error('Dopravci nenalezeni')
                const lookupBrigadiersRes = await ContractorManager.getManager().getBrigadiersLookupAsync()
                if (!lookupBrigadiersRes) throw new Error('Brigádníci nenalezeni')
                setLookupCarriers(lookupCarriersRes.items)
                setLookupBrigadiers(lookupBrigadiersRes.items)
            } catch (error) {
                dispatch(displayMessage({ message: error, type: 'error' }))
                setLoadingLookupData(false)
            }
            setLoadingLookupData(false)
        }

        getLookupData()
    }, [setLoadingLookupData])

    useEffect(() => {
        getProductQuantities()
    }, [event?.startsAt, event?.endsAt])

    const addNewClient = () => {
        const newClientName = event?.description.match(/[Kk]lient: (.*?)\n/g)
        const newContactName = event?.description.match(/[Kk]ontaktní osoba: (.*?)\n/g)
        const newClientEmail = event?.description.match(/[eE]mail: (.*?)\n/g)
        const newClientNumber = event?.description.match(/[Cc]islo: (.*?)\n/g)
        const params = new URLSearchParams()
        if (newClientName) params.append('name', newClientName[0].split('lient: ')[1].split('\n')[0])
        if (newContactName) params.append('contact', newContactName[0].split('ontaktní osoba: ')[1].split('\n')[0])
        if (newClientEmail) params.append('email', newClientEmail[0].split('mail: ')[1].split('\n')[0])
        if (newClientNumber) params.append('phone', newClientNumber[0].split('islo: ')[1].split('\n')[0])
        saveEvent()
        navigate(`${RouteConstants.addClient}?${params.toString()}`)
    }

    const deleteEvent = async () => {
        try {
            await EventManager.getManager().deleteEventAsync(finalId)
        } catch (error) {
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        dispatch(displayMessage({ message: 'Událost smazána', type: 'success' }))
        closeDialog ? closeDialog() : navigate(RouteConstants.events)
        dispatch(setSaveState(true))
    }

    const createInvoice = async (
        invoiceIssuerId: string,
        productsText: string,
        orderNumber: string,
        paymentType: InvoicePaymentType,
        issueDate?: Date,
        taxDate?: Date,
        dueDate?: Date,
    ) => {
        try {
            setLoadingData(true)
            const invoiceRes = await EventManager.getManager().createInvoiceAsync(finalId, {
                invoiceIssuerId: invoiceIssuerId,
                productsText: productsText,
                orderNumber: orderNumber ?? '',
                issueDate: issueDate && convertTimeToLocaleTime(issueDate),
                taxDate: taxDate && convertTimeToLocaleTime(taxDate),
                dueDate: dueDate && convertTimeToLocaleTime(dueDate),
                paymentType: paymentType,
            })
            if (!invoiceRes) throw new Error('Fakturu se nepodařilo vytvořit')
            setInvoice(invoiceRes)
        } catch (error) {
            dispatch(displayMessage({ message: error, type: 'error' }))
            setLoadingData(false)
        }
        setLoadingData(false)
    }

    const deleteInvoice = async (invoiceId: string) => {
        try {
            setLoadingData(true)
            await InvoiceManager.getManager().deleteInvoiceAsync(invoiceId)
            setInvoice(undefined)
        } catch (error) {
            dispatch(displayMessage({ message: error, type: 'error' }))
            setLoadingData(false)
        }
        dispatch(displayMessage({ message: 'Faktura odstraněna', type: 'success' }))
        setLoadingData(false)
    }

    const saveEvent = async () => {
        if (!event) return
        setLoadingData(true)
        try {
            const eventRes = await EventManager.getManager().updateEventAsync(finalId, {
                ...event,
                products: event.products
                    .filter((product) => product.productId !== '')
                    .map((product) => ({
                        ...product,
                        operatingTime: getTimeSpanFromFormattedTimeSpan(product.operatingTime),
                    })),
                contractors: event.contractors.filter((contractor) => contractor.contractorId !== ''),
            })
            if (eventRes) setEvent(eventRes)
        } catch (error) {
            setLoadingData(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        setLoadingData(false)
        closeDialog && closeDialog()
        dispatch(displayMessage({ message: 'Událost uložena', type: 'success' }))
    }

    const handleSelectContractorChange = (
        newValue: IdNameDto,
        oldId: string,
        index: number,
        sortBy: keyof ContractorOfEventDto | undefined,
        sortOrder: 'asc' | 'desc'
    ) => {
        if (!event) return

        const isContractorInList = event.contractors.some((localContractor) => localContractor.contractorId === newValue.id)
        if (isContractorInList) {
            dispatch(
                displayMessage({
                    message: 'Pracovník je již v přiřazen k této události',
                    type: 'warning',
                })
            )
            return
        }

        const newContractor = contractorList?.find((c) => c.id === newValue.id)

        const brigadiers = event.contractors.filter((contractor) => contractor.contractorType !== ContractorType.Carrier)
        const carriers = event.contractors.filter((contractor) => contractor.contractorType === ContractorType.Carrier)

        if (newContractor?.type === ContractorType.Carrier) {
            const newCarriers = carriers.map((contractor) => {
                if (contractor.contractorId === oldId) {
                    return {
                        ...contractor,
                        contractorId: newValue.id,
                        contractorName: newContractor?.name + ' ' + newContractor?.lastName,
                    }
                }
                return contractor
            })

            setEvent({
                ...event,
                contractors: [...newCarriers, ...brigadiers],
            })
        } else {
            const newBrigadiers = brigadiers
                .sort((a: ContractorOfEventDto, b: ContractorOfEventDto) => {
                    if (sortBy) {
                        if ((a[sortBy] ?? 0) < (b[sortBy] ?? 0)) {
                            return sortOrder === 'asc' ? -1 : 1
                        }
                        if ((a[sortBy] ?? 0) > (b[sortBy] ?? 0)) {
                            return sortOrder === 'asc' ? 1 : -1
                        }
                    }
                    return 0
                })
                .map((contractor, i) => {
                    if (i === index) {
                        return {
                            ...contractor,
                            contractorId: newValue.id,
                            contractorName: newContractor?.name + ' ' + newContractor?.lastName,
                        }
                    }
                    return contractor
                })

            setEvent({
                ...event,
                contractors: [...newBrigadiers, ...carriers],
            })
        }
    }

    const getEventsOfProduct = async (productId: string, eventsFrom?: Date, eventsTo?: Date) => {
        if (!event?.products) return
        setAreProductQuantitiesLoading(true)
        try {
            const productEventsRes = await ProductManager.getManager().getEventsOfProductAsync(productId, eventsFrom, eventsTo)
            if (productEventsRes) {
                setAreProductQuantitiesLoading(false)
                return productEventsRes
            }
        } catch (error) {
            dispatch(displayMessage({ message: error, type: 'error' }))
            setAreProductQuantitiesLoading(false)
        }
    }

    if (loadingData) return <ScreenLoadingPlaceholder />

    if (!event)
        return (
            <Typography variant='h4' color='error' textAlign='center' marginY={theme.spacing(3)}>
                Tato událost neexistuje
            </Typography>
        )

    return (
        <CustomContainer customPadding={theme.spacing(2) + ' ' + theme.spacing(3)}>
            <Grid container rowSpacing={4} columnSpacing={4} mb={3}>
                <Grid item xs={12} lg={6}>
                    <EventBasicInformation
                        event={event}
                        setEvent={setEvent}
                        invoice={invoice}
                        createInvoice={createInvoice}
                        onDeleteInvoice={deleteInvoice}
                        onAddNewClient={addNewClient}
                        recalculateOperatingTimeForEachProduct={recalculateOperatingTimeForEachProduct}
                    />
                </Grid>

                <Grid item xs={12} lg={6}>
                    <Grid container rowSpacing={3}>
                        <Grid item xs={12}>
                            {(loadingLookupData || loadingData) ? (
                                <CircularProgress />
                            ) : (
                                <ProductTable
                                    event={event}
                                    setEvent={setEvent}
                                    areProductQuantitiesLoading={areProductQuantitiesLoading}
                                    lookUpProducts={lookupProducts ?? []}
                                    getUsedProducts={() => getUsedProducts(event, productQuantities)}
                                    setIsProductChanged={setIsProductChanged}
                                    recalculateProductQuantities={() => getProductQuantities()}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {(loadingLookupData || loadingData) ? (
                                <CircularProgress />
                            ) : (
                                <BrigadierTable
                                    event={event}
                                    setEvent={setEvent}
                                    contractorList={
                                        contractorList?.filter(
                                            (contractor: ContractorDto) => contractor.type !== ContractorType.Carrier
                                        ) ?? []
                                    }
                                    isContractorListLoading={loadingTableData}
                                    contractors={lookupBrigadiers ?? []}
                                    handleSelectContractorChange={handleSelectContractorChange}
                                    onValueChanged={setIsContractorChanged}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant='h6' color='primary' mb={2}>
                                Poznámka pro brigádníky
                            </Typography>
                            <CustomTextField
                                fullWidth
                                variant='outlined'
                                placeholder='Poznámka pro brigádníky'
                                size='small'
                                rows={2}
                                multiline
                                value={event.notesForBrigadiers}
                                onChange={(e) => setEvent({ ...event, notesForBrigadiers: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            {(loadingLookupData || loadingData) ? (
                                <CircularProgress />
                            ) : (
                                <CarrierTable
                                    event={event}
                                    setEvent={setEvent}
                                    contractors={lookupCarriers ?? []}
                                    isContractorListLoading={loadingTableData}
                                    contractorList={
                                        contractorList?.filter(
                                            (contractor: ContractorDto) => contractor.type === ContractorType.Carrier
                                        ) ?? []
                                    }
                                    handleSelectContractorChange={handleSelectContractorChange}
                                    onValueChanged={setIsContractorChanged}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container rowSpacing={3} columnSpacing={3} mb={5}>
                <Grid item xs={12} lg={3}>
                    {loadingInvoiceData ? (
                        <CircularProgress />
                    ) : (
                        <EventInvoiceInfo
                            costs={event.costs}
                            profit={event.profit}
                            revenue={event.revenue}
                        />
                    )}
                </Grid>
                <Grid item xs={12} lg={4}>
                    <EventStateHistory
                        orderStateChanges={event.orderStateChanges}
                        paymentStateChanges={event.paymentStateChanges}
                    />
                </Grid>
                <Grid item xs={12} lg={5}>
                    {loadingInvoiceData ? (
                        <CircularProgress />
                    ) : (
                        <EventSurveys emailMessageLog={event.emailMessagesLogs} emailTemplates={emailTemplatesList ?? []} />
                    )}
                </Grid>
            </Grid>
            <BottomBar onDelete={deleteEvent} onSave={saveEvent} defaultRoute={RouteConstants.events} deletedEntityName={event.name} disabled={
                loadingData || loadingInvoiceData || loadingTableData || loadingLookupData
            } />
        </CustomContainer>
    )
}

export default EventDetail
